import { listen } from "../vendor/kicanvas/src/base/events";
import { Color } from "../vendor/kicanvas/src/base/color";
import { Vec2 } from "../vendor/kicanvas/src/base/math";
import { CSS, css, html, provideContext } from "../vendor/kicanvas/src/base/web-components";
import { KiCanvasMouseMoveEvent, KiCanvasSelectEvent } from "../vendor/kicanvas/src/viewers/base/events";
import { Viewport } from "../vendor/kicanvas/src/viewers/base/viewport";
import { BoardViewer } from "../vendor/kicanvas/src/viewers/board/viewer";
import { LayerNames } from "../vendor/kicanvas/src/viewers/board/layers";
import { CircleSet, PolygonSet, PolylineSet } from "../vendor/kicanvas/src/graphics/webgl/vector";
import { KCBoardViewerElement } from "../vendor/kicanvas/src/kicanvas/elements/kc-board/viewer";
import { KCBoardAppElement } from "../vendor/kicanvas/src/kicanvas/elements/kc-board/app";
import "../vendor/kicanvas/src/kicanvas/elements/common/context-menu";
import { Project, type ProjectPage } from "../vendor/kicanvas/src/kicanvas/project";
import { LocalFileSystem } from "../vendor/kicanvas/src/kicanvas/services/vfs";
import { Preferences } from "../vendor/kicanvas/src/kicanvas/preferences";
import themes from "../vendor/kicanvas/src/kicanvas/themes";
import { sprites_url } from "../vendor/kicanvas/src/kicanvas/icons/sprites";
import { KCUIElement, KCUIIconElement } from "../vendor/kicanvas/src/kc-ui";
import { KicadPCB, type Theme } from "../vendor/kicanvas/src/kicad";
import kcUiStyles from "../vendor/kicanvas/src/kc-ui/kc-ui.css";

type Rect = { x: number; y: number; w: number; h: number };
type BoardSide = "F" | "B";
type NetFocusShape =
  | { kind: "line"; a: [number, number]; b: [number, number] }
  | { kind: "quad"; points: [number, number][] }
  | { kind: "via"; outer: [number, number][]; inner?: [number, number][] };

type ViewConfig = {
  id?: string;
  label?: string;
  side?: BoardSide;
  photo: string;
  render?: string;
  H_render_to_photo: number[] | number[][];
  render_bbox: Rect;
  match?: Record<string, unknown>;
};

type RawConfig = {
  board: string;
  views?: ViewConfig[];
} & ViewConfig;

type RuntimeViewConfig = Omit<ViewConfig, "H_render_to_photo"> & { H: number[]; side: BoardSide };
type RuntimeConfig = { board: string; views: RuntimeViewConfig[] };
type ViewPane = {
  config: RuntimeViewConfig;
  pane: HTMLDivElement;
  title: HTMLDivElement;
  stage: HTMLDivElement;
  photo: HTMLImageElement;
  canvas: HTMLCanvasElement;
  viewer: WarpedBoardViewer;
  photoScale: number;
  zoom: number;
  opacity: number;
};

KCUIIconElement.sprites_url = sprites_url;

const RENDER_SIZE = 1200;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 12;
const FOCUS_CANVAS_MAX_SIDE = 4096;
const FOCUS_CANVAS_MAX_PIXELS = 6_000_000;
let runtimeConfig: RuntimeConfig | null = null;

const statusEl = document.getElementById("status")!;
const selectionEl = document.getElementById("selection")!;

function setStatus(msg: string) { statusEl.textContent = msg; }

function flat3(H: number[] | number[][]): number[] {
  if (Array.isArray(H[0])) {
    const h = H as number[][];
    return [h[0][0], h[0][1], h[0][2], h[1][0], h[1][1], h[1][2], h[2][0], h[2][1], h[2][2]];
  }
  return H as number[];
}

function normalizeConfig(raw: RawConfig): RuntimeConfig {
  const rawViews = raw.views?.length ? raw.views : [{
    id: raw.id,
    label: raw.label,
    side: raw.side,
    photo: raw.photo,
    render: raw.render,
    H_render_to_photo: raw.H_render_to_photo,
    render_bbox: raw.render_bbox,
    match: raw.match,
  }];
  return {
    board: raw.board,
    views: rawViews.map((view, idx) => ({
      ...view,
      id: view.id ?? `view-${idx + 1}`,
      label: view.label ?? (view.side === "B" ? "Back" : view.side === "F" ? "Front" : `View ${idx + 1}`),
      side: view.side ?? (idx === 0 ? "F" : "B"),
      H: flat3(view.H_render_to_photo),
    })),
  };
}

function mat3Mul(a: number[], b: number[]): number[] {
  const o = new Array(9).fill(0);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      o[r * 3 + c] = a[r * 3 + 0] * b[0 * 3 + c] + a[r * 3 + 1] * b[1 * 3 + c] + a[r * 3 + 2] * b[2 * 3 + c];
    }
  }
  return o;
}

function mat3Inv(m: number[]): number[] | null {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const D = -(b * i - c * h);
  const E = a * i - c * g;
  const F = -(a * h - b * g);
  const G = b * f - c * e;
  const H = -(a * f - c * d);
  const I = a * e - b * d;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-12) return null;
  const s = 1 / det;
  return [A * s, D * s, G * s, B * s, E * s, H * s, C * s, F * s, I * s];
}

function project(H: number[], x: number, y: number): [number, number] {
  const w = H[6] * x + H[7] * y + H[8];
  return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w];
}

function homographyToMatrix3d(H: number[], scaleA = 1, scaleB = 1): string {
  // CSS matrix3d is column-major. This is the same convention used in the
  // sibling video overlay tracker.
  const m = [
    H[0] * scaleB / scaleA, H[3] * scaleB / scaleA, 0, H[6] / scaleA,
    H[1] * scaleB / scaleA, H[4] * scaleB / scaleA, 0, H[7] / scaleA,
    0, 0, 1, 0,
    H[2] * scaleB, H[5] * scaleB, 0, H[8],
  ];
  return `matrix3d(${m.map((v) => v.toPrecision(10)).join(",")})`;
}

function clientTransformFor(stage: HTMLElement, HcanvasToPhoto: number[], photoScale: number): number[] {
  const rect = stage.getBoundingClientRect();
  const SphotoToClient = [photoScale, 0, rect.left, 0, photoScale, rect.top, 0, 0, 1];
  return mat3Mul(SphotoToClient, HcanvasToPhoto);
}

function itemLabel(item: any): string {
  if (!item) return "none";
  if (item.reference) return `Footprint ${item.reference}`;
  if (item.netname) return `Net ${item.netname}`;
  return item.constructor?.name ?? String(item);
}

function basenameFromUrl(path: string): string {
  const url = new URL(path, window.location.href);
  const parts = url.pathname.split("/");
  return decodeURIComponent(parts.at(-1) || "board.kicad_pcb");
}

function mapUndefinedLayersByFootprintSide(text: string): string {
  // kicad-cli's Eagle import emits many bitmap/polygon silk pieces as
  // `UNDEFINED`. They live inside footprints, so infer their side from the
  // parent footprint layer instead of forcing everything to front silk.
  const footprintStarts = [...text.matchAll(/^\s*\(footprint\s/mg)].map((m) => m.index ?? 0);
  if (!footprintStarts.length) return text.replaceAll('"UNDEFINED"', '"F.SilkS"');

  let out = "";
  let cursor = 0;
  for (let i = 0; i < footprintStarts.length; i++) {
    const start = footprintStarts[i];
    const end = footprintStarts[i + 1] ?? text.length;
    out += text.slice(cursor, start).replaceAll('"UNDEFINED"', '"F.SilkS"');
    const block = text.slice(start, end);
    const footprintLayer = block.match(/\(layer\s+"([^"]+)"\)/)?.[1] ?? "F.Cu";
    const silkLayer = footprintLayer.startsWith("B.") ? "B.SilkS" : "F.SilkS";
    out += block.replaceAll('"UNDEFINED"', `"${silkLayer}"`);
    cursor = end;
  }
  out += text.slice(cursor).replaceAll('"UNDEFINED"', '"F.SilkS"');
  return out;
}

function sanitizeBoardText(text: string): string {
  text = mapUndefinedLayersByFootprintSide(text);
  // KiCanvas's parser doesn't understand a couple of KiCad 8/9 metadata fields
  // emitted by the Eagle import. Leaving them in produces thousands of giant
  // console warnings and can stall page initialization in Chromium.
  text = text.replace(/\s+\(duplicate_pad_numbers_are_jumpers\s+(?:yes|no)\)/g, "");
  text = text.replace(/\s+\(embedded_fonts\s+(?:yes|no)\)/g, "");
  text = text.replace(/\s+\((?:tenting|covering|plugging)\s+\(front\s+(?:yes|no)\)\s+\(back\s+(?:yes|no)\)\)/g, "");
  text = text.replace(/\s+\((?:capping|filling)\s+(?:yes|no)\)/g, "");
  text = text.replace(/\s+\(arrow_direction\s+\w+\)/g, "");

  // The Eagle importer also emits net references as `(net "GND")`. KiCanvas
  // expects normal KiCad net records: top-level `(net 1 "GND")` plus numeric
  // item references `(net 1)` / pad references `(net 1 "GND")`. Normalize this
  // in-memory so nets/tracks/pads become clickable and named in KiCanvas.
  const netNames = Array.from(text.matchAll(/\(net\s+"([^"]+)"\)/g), (m) => m[1]);
  const uniqueNetNames = [...new Set(netNames)].sort();
  if (uniqueNetNames.length) {
    const netNumber = new Map(uniqueNetNames.map((name, idx) => [name, idx + 1]));
    text = text.replace(/\(net\s+"([^"]+)"\)/g, (_all, name: string) => `(net ${netNumber.get(name)} "${name}")`);

    if (!/^\s*\(net\s+0\s+/m.test(text)) {
      const netDefs = [`\t(net 0 "")`, ...uniqueNetNames.map((name) => `\t(net ${netNumber.get(name)} "${name}")`)].join("\n");
      const insertAt = text.search(/\n\s*\((footprint|segment|via|zone|gr_)/);
      if (insertAt !== -1) {
        text = `${text.slice(0, insertAt)}\n${netDefs}${text.slice(insertAt)}`;
      }
    }
  }

  return text;
}

function applyPhotoOverlayTheme(theme: any) {
  // KiCanvas themes are designed for opaque dark/light CAD backgrounds. Over a
  // real PCB photo we want saturated, non-white, semi-transparent strokes.
  theme.background = Color.transparent_black;
  theme.copper.f = Color.from_css("rgba(255, 32, 122, 0.78)");
  theme.copper.b = Color.from_css("rgba(40, 170, 255, 0.45)");
  theme.copper.in1 = Color.from_css("rgba(0, 180, 120, 0.25)");
  theme.copper.in2 = Color.from_css("rgba(255, 130, 0, 0.25)");
  theme.f_silks = Color.from_css("rgba(0, 225, 255, 0.88)");
  theme.b_silks = Color.from_css("rgba(80, 170, 255, 0.44)");
  theme.f_mask = Color.from_css("rgba(120, 0, 180, 0.18)");
  theme.f_paste = Color.from_css("rgba(255, 220, 80, 0.45)");
  theme.edge_cuts = Color.from_css("rgba(40, 255, 120, 0.95)");
  theme.pad_through_hole = Color.from_css("rgba(255, 185, 40, 0.86)");
  theme.pad_plated_hole = Color.from_css("rgba(255, 150, 0, 0.76)");
  theme.via_through = Color.from_css("rgba(255, 185, 40, 0.72)");
  theme.via_hole = Color.from_css("rgba(0, 0, 0, 0)");
  theme.non_plated_hole = Color.from_css("rgba(40, 255, 120, 0.8)");
  theme.f_fab = Color.from_css("rgba(0, 180, 255, 0.22)");
  theme.f_crtyd = Color.from_css("rgba(255, 0, 180, 0.22)");
  theme.footprint_text_invisible = Color.from_css("rgba(0, 225, 255, 0.42)");
  theme.worksheet = Color.transparent_black;
  theme.grid = Color.transparent_black;
  theme.grid_axes = Color.transparent_black;
  return theme;
}

function cloneThemeValue<T>(value: T): T {
  if (value instanceof Color) return value.copy() as T;
  if (Array.isArray(value)) return value.map(cloneThemeValue) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, cloneThemeValue(v)])) as T;
  }
  return value;
}

function installPilcrowTheme(): Theme {
  const originalByName = themes.by_name.bind(themes);
  const originalList = themes.list.bind(themes);
  const pilcrow = cloneThemeValue(originalByName("witchhazel")) as Theme;
  pilcrow.name = "pilcrow";
  pilcrow.friendly_name = "Pilcrow";
  applyPhotoOverlayTheme(pilcrow.board);

  (themes as any).default = pilcrow;
  (themes as any).by_name = (name: string) => (name === "pilcrow" ? pilcrow : originalByName(name));
  (themes as any).list = () => [pilcrow, ...originalList().filter((t) => t.name !== "pilcrow")];
  Preferences.INSTANCE.theme = pilcrow;
  return pilcrow;
}

const PILCROW_THEME = installPilcrowTheme();

class WarpedBoardViewer extends BoardViewer {
  screenTransform: number[] = [1, 0, 0, 0, 1, 0, 0, 0, 1];
  activeNetNumber: number | null = null;
  lastClientX = 0;
  lastClientY = 0;
  private renderBBox: Rect | null = null;
  private webglShaders: { circle: any; polygon: any; polyline: any } | null = null;

  override async setup() {
    this.renderer = this.disposables.add(this.create_renderer(this.canvas));
    await this.renderer.setup();
    // KiCanvas's WebGL primitive shader statics are process-wide, but each
    // canvas has its own WebGL context. Remember the shader programs created
    // for this viewer and restore them before any retained layer is rebuilt.
    this.webglShaders = {
      circle: CircleSet.shader,
      polygon: PolygonSet.shader,
      polyline: PolylineSet.shader,
    };
    this.viewport = this.disposables.add(new Viewport(this.renderer, () => this.on_viewport_change()));

    if (this.interactive) {
      // Keep KiCanvas picking/click listeners, but intentionally do NOT call
      // viewport.enable_pan_and_zoom(): the board camera must stay locked to
      // the static photo registration.
      this.disposables.add(listen(this.canvas, "mousemove", (e) => this.on_mouse_change(e)));
      this.disposables.add(listen(this.canvas, "click", () => {
        const items = this.layers.query_point(this.mouse_position);
        this.on_pick(this.mouse_position, items);
      }));
    }

    this.setup_finished.open();
  }

  private activateWebglContextShaders() {
    if (!this.webglShaders) return;
    CircleSet.shader = this.webglShaders.circle;
    PolygonSet.shader = this.webglShaders.polygon;
    PolylineSet.shader = this.webglShaders.polyline;
  }

  override paint(): void {
    this.activateWebglContextShaders();
    super.paint();
  }

  updateMouseFromClient(clientX: number, clientY: number): Vec2 | null {
    this.lastClientX = clientX;
    this.lastClientY = clientY;
    const inv = mat3Inv(this.screenTransform);
    if (!inv) return null;
    const [lx, ly] = project(inv, clientX, clientY);
    const newPosition = this.viewport.camera.screen_to_world(new Vec2(lx, ly));
    if (this.mouse_position.x !== newPosition.x || this.mouse_position.y !== newPosition.y) {
      this.mouse_position.set(newPosition);
      this.dispatchEvent(new KiCanvasMouseMoveEvent(this.mouse_position));
    }
    return newPosition;
  }

  pickAtClient(clientX: number, clientY: number): boolean {
    const world = this.updateMouseFromClient(clientX, clientY);
    if (!world || !this.layers) return false;
    const hits = Array.from(this.layers.query_point(world));
    if (!hits.length) return false;

    // In photo-overlay mode component clicks should feel direct. KiCanvas's
    // stock picker opens a context menu when a footprint and a pad/track net
    // overlap, which makes component clicks look like they did nothing. Prefer
    // the topmost footprint bbox when the click intersects one; pure trace/pad
    // clicks still fall through to KiCanvas's normal net picker.
    const seen = new Set<any>();
    const footprints: any[] = [];
    for (const hit of hits) {
      const item = hit.bbox.context;
      if (item?.reference && !seen.has(item)) {
        seen.add(item);
        footprints.push(item);
      }
    }
    if (footprints.length) {
      this.handleItemClick(footprints[0]);
      return true;
    }

    this.on_pick(world, hits[Symbol.iterator]() as any);
    return true;
  }

  protected override on_mouse_change(e: MouseEvent) {
    this.updateMouseFromClient(e.clientX, e.clientY);
  }

  lockToRenderBBox(renderBBox: Rect) {
    this.renderBBox = renderBBox;
    const edge = this.layers.by_name(LayerNames.edge_cuts)?.bbox;
    if (!edge?.valid) {
      // Avoid recursive zoom_to_board() fallback; just keep KiCanvas's current
      // camera if edge cuts failed to paint.
      this.draw();
      return;
    }

    const zX = renderBBox.w / edge.w;
    const zY = renderBBox.h / edge.h;
    const z = (zX + zY) / 2;
    this.viewport.camera.zoom = z;
    const dx = (renderBBox.x + renderBBox.w / 2 - RENDER_SIZE / 2) / z;
    const dy = (renderBBox.y + renderBBox.h / 2 - RENDER_SIZE / 2) / z;
    this.viewport.camera.center.set(
      edge.x + edge.w / 2 + (this.viewport.camera.flipped ? dx : -dx),
      edge.y + edge.h / 2 - dy,
    );
    this.draw();
  }

  override zoom_to_page(): void {
    if (this.renderBBox) {
      this.lockToRenderBBox(this.renderBBox);
    } else {
      super.zoom_to_page();
    }
  }

  override zoom_to_board(): void {
    if (this.renderBBox) {
      this.lockToRenderBBox(this.renderBBox);
    } else {
      super.zoom_to_board();
    }
  }

  override zoom_to_selection(): void {
    // Do not let the built-in toolbar change KiCanvas's internal camera: the
    // photo registration assumes a frozen board-canvas camera. Outer zoom/pan
    // belongs to the wrapper.
    if (this.renderBBox) {
      this.lockToRenderBBox(this.renderBBox);
    } else {
      super.zoom_to_selection();
    }
  }

  protected override on_draw() {
    this.activateWebglContextShaders();
    // Keep the WebGL backing buffer transparent. KiCanvas's WebGL renderer only
    // applies clearColor during setup, so make the invariant explicit here.
    (this.renderer as any).gl?.clearColor(0, 0, 0, 0);
    super.on_draw();
  }

  private netNumberFor(item: any): number | null {
    if (!item || !("net" in item)) return null;
    return typeof item.net === "number" ? item.net : (item.net?.number ?? null);
  }

  override handleItemClick(item: unknown) {
    const net = this.netNumberFor(item as any);
    if ((item as any)?.reference) {
      this.activeNetNumber = null;
      super.handleItemClick(item);
      return;
    }
    if (net != null) {
      // Net/path clicks should not leave a previous component focus active.
      this.select(null);
      this.highlight_net(net);
      return;
    }
    this.activeNetNumber = null;
    super.handleItemClick(item);
    this.dispatchEvent(new CustomEvent("pilcrow:net-highlight", { detail: { net: null } }));
  }

  applyNetHighlight(net: number, dispatch = false) {
    this.activeNetNumber = net;
    this.activateWebglContextShaders();
    super.highlight_net(net);
    const overlay = this.layers.overlay;
    if (overlay.graphics) {
      overlay.opacity = 1;
      overlay.graphics.composite_operation = "source-over";
    }
    if (dispatch) this.dispatchEvent(new CustomEvent("pilcrow:net-highlight", { detail: { net } }));
  }

  override highlight_net(net: number) {
    this.applyNetHighlight(net, true);
  }

  clearNetHighlight(dispatch = false) {
    this.activeNetNumber = null;
    this.layers?.overlay?.clear();
    this.draw();
    if (dispatch) this.dispatchEvent(new CustomEvent("pilcrow:net-highlight", { detail: { net: null } }));
  }

  protected override paint_selected() {
    // The DOM focus overlay draws selection. Keep KiCanvas's overlay layer clear
    // so the selected component never gets filled/washed out over the photo.
    this.layers.overlay.clear();
    this.draw();
  }
}

class WarpedBoardViewerElement extends KCBoardViewerElement {
  private viewsContainer!: HTMLDivElement;
  private primaryPaneEl!: HTMLDivElement;
  private primaryTitle!: HTMLDivElement;
  private stage!: HTMLDivElement;
  private photo!: HTMLImageElement;
  private focusCanvas!: HTMLCanvasElement;
  private overlayControls!: HTMLDivElement;
  private opacityInput!: HTMLInputElement;
  private fitButton!: HTMLButtonElement;
  private contextMenu!: any;
  private resizeObserver?: ResizeObserver;
  declare viewer: WarpedBoardViewer;
  private panes: ViewPane[] = [];
  private opacity = 0.72;
  private panListenerInstalled = false;
  private overlayControlsInstalled = false;
  private activePan: { pane: ViewPane; lastX: number; lastY: number; moved: boolean } | null = null;
  private suppressNextClick = false;
  private selectedItem: any = null;
  private highlightedNet: number | null = null;
  private focusRaf = 0;
  private syncingSelection = false;
  private syncingPanZoom = false;
  private syncPanZoomRaf = 0;
  private pendingPanZoomSource: ViewPane | null = null;
  private suppressScrollSync = new Set<ViewPane>();

  protected override make_viewer(): WarpedBoardViewer {
    // Apply the photo-overlay theme before viewer.setup(), otherwise WebGL's
    // initial clearColor is the normal opaque KiCanvas background.
    const viewer = new WarpedBoardViewer(this.canvas, !this.disableinteraction, PILCROW_THEME.board);
    viewer.contextMenuCallback = (_screenX, _screenY, items, onSelect) => {
      // BoardViewer's default context-menu coordinates are canvas-local. Under
      // CSS perspective warp they are not viewport/client coordinates, so use
      // the last raw mouse event position instead.
      this.contextMenu?.show(viewer.lastClientX, viewer.lastClientY, items, onSelect);
    };
    return viewer;
  }

  override render() {
    this.photo = html`<img class="photo" alt="PCB photo" />` as HTMLImageElement;
    this.canvas = html`<canvas class="boardCanvas"></canvas>` as HTMLCanvasElement;
    this.focusCanvas = html`<canvas id="focusCanvas"></canvas>` as HTMLCanvasElement;
    this.opacityInput = html`<input id="overlayOpacity" type="range" min="0" max="1" step="0.05" value="${this.opacity}" aria-label="Board overlay opacity" />` as HTMLInputElement;
    this.fitButton = html`<button id="overlayFit" type="button">Fit</button>` as HTMLButtonElement;
    this.overlayControls = html`<div id="overlayControls" role="toolbar" aria-label="Photo overlay controls">
      <label for="overlayOpacity"><span>Opacity</span>${this.opacityInput}</label>
      ${this.fitButton}
    </div>` as HTMLDivElement;
    this.contextMenu = html`<kc-context-menu></kc-context-menu>`;
    this.stage = html`<div class="stage controlStage">${this.photo}${this.canvas}</div>` as HTMLDivElement;
    this.primaryTitle = html`<div class="paneTitle"></div>` as HTMLDivElement;
    this.primaryPaneEl = html`<div id="controlPane" class="viewPane">${this.primaryTitle}${this.stage}</div>` as HTMLDivElement;
    this.viewsContainer = html`<div id="views">${this.primaryPaneEl}</div>` as HTMLDivElement;

    this.patchCanvasForWarp(this.canvas);

    return html`<style>
        :host {
          display: block;
          touch-action: none;
          width: 100%;
          height: 100%;
          overflow: hidden;
          position: relative;
          background: #000;
        }
        #views {
          width: 100%;
          height: 100%;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(480px, 1fr));
          gap: 1px;
          background: var(--resizer-bg, #282634);
        }
        .viewPane {
          min-width: 0;
          min-height: 0;
          overflow: auto;
          position: relative;
          background: #000;
          touch-action: none;
        }
        .paneTitle {
          position: absolute;
          left: 0;
          top: 0;
          z-index: 4;
          display: inline-block;
          margin: 0.5em;
          padding: 0.35em 0.55em;
          color: var(--button-toolbar-fg);
          background: var(--button-toolbar-bg);
          border: 1px solid var(--button-toolbar-bg);
          border-radius: 0.25em;
          box-shadow: var(--input-hover-shadow);
          font-size: 0.82em;
          font-weight: 300;
          user-select: none;
          pointer-events: none;
        }
        .stage {
          position: relative;
          transform-origin: 0 0;
          background: #000;
          visibility: hidden;
          opacity: 0;
          overflow: hidden;
        }
        .photo {
          position: absolute;
          left: 0;
          top: 0;
          display: block;
          user-select: none;
          pointer-events: none;
          z-index: 0;
        }
        .boardCanvas {
          position: absolute;
          left: 0;
          top: 0;
          width: ${RENDER_SIZE}px;
          height: ${RENDER_SIZE}px;
          transform-origin: 0 0;
          opacity: 0;
          visibility: hidden;
          touch-action: none;
          pointer-events: none;
          z-index: 1;
        }
        #focusCanvas {
          position: fixed;
          left: 0;
          top: 0;
          pointer-events: none;
          z-index: 20;
        }
        #overlayControls {
          position: fixed;
          left: 12px;
          top: 12px;
          z-index: 30;
          display: flex;
          visibility: hidden;
          align-items: center;
          gap: 0.5em;
          padding: 0.5em;
          color: var(--button-toolbar-fg);
          background: var(--button-toolbar-bg);
          border: 1px solid var(--button-toolbar-bg);
          border-radius: 0.25em;
          box-shadow: var(--input-hover-shadow);
          pointer-events: auto;
          user-select: none;
        }
        #overlayControls label {
          display: inline-flex;
          align-items: center;
          gap: 0.5em;
          color: var(--button-toolbar-fg);
          font-size: 0.9em;
          font-weight: 300;
          white-space: nowrap;
        }
        #overlayControls input[type="range"] {
          width: 112px;
          accent-color: var(--input-accent);
        }
        #overlayControls button {
          appearance: none;
          border: 1px solid var(--button-toolbar-bg);
          border-radius: 0.25em;
          padding: 0.5em;
          color: var(--button-toolbar-fg);
          background: var(--button-toolbar-bg);
          font: inherit;
          font-size: 0.9em;
          font-weight: 300;
          cursor: pointer;
        }
        #overlayControls button:hover {
          color: var(--button-toolbar-hover-fg);
          background: var(--button-toolbar-hover-bg);
        }
      </style>${this.viewsContainer}${this.focusCanvas}${this.overlayControls}${this.contextMenu}`;
  }

  override async load(src: ProjectPage) {
    this.loaded = false;
    // The Eagle import includes board dimension drawings; they are useful CAD
    // annotations but not part of the physical board in the photo.
    const doc: any = src.document;
    if (Array.isArray(doc?.drawings)) {
      doc.drawings = doc.drawings.filter((d: any) => d.constructor?.name !== "Dimension");
    }
    await super.load(src);
    await this.viewer.loaded;
    await this.configureWarpedCanvas(src);
  }

  override disconnectedCallback() {
    this.resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  private patchCanvasForWarp(canvas: HTMLCanvasElement) {
    // KiCanvas's WebGL renderer asks for alpha:false. Force alpha:true so the
    // board canvas can be transparent over the photo.
    const originalGetContext = canvas.getContext.bind(canvas) as HTMLCanvasElement["getContext"];
    (canvas as any).getContext = (type: string, attrs?: any) => {
      if (type === "webgl2") return originalGetContext(type as any, { ...(attrs || {}), alpha: true, premultipliedAlpha: false, preserveDrawingBuffer: true });
      return originalGetContext(type as any, attrs);
    };

    // KiCanvas/WebGL sizes itself from getBoundingClientRect(). After a CSS
    // perspective transform that rect is the transformed AABB, not the source
    // render size. Pin renderer sizing to the unwarped render coordinate system.
    (canvas as any).getBoundingClientRect = () => ({
      x: 0,
      y: 0,
      left: 0,
      top: 0,
      right: RENDER_SIZE,
      bottom: RENDER_SIZE,
      width: RENDER_SIZE,
      height: RENDER_SIZE,
      toJSON() { return this; },
    });
  }

  private async configureWarpedCanvas(src: ProjectPage) {
    const config = runtimeConfig;
    if (!config?.views.length) throw new Error("runtime config was not loaded");

    this.overlayControls.style.visibility = "hidden";
    this.panes = [];
    this.viewsContainer.querySelectorAll(".viewPane.extra").forEach((el) => el.remove());

    this.panes.push(await this.createPane(config.views[0], src, true));
    for (const view of config.views.slice(1)) {
      this.panes.push(await this.createPane(view, src, false));
    }
    this.viewsContainer.style.gridTemplateColumns = `repeat(${this.panes.length}, minmax(0, 1fr))`;

    await Promise.all(this.panes.map((pane) => pane.photo.decode().catch(() => undefined)));
    for (const pane of this.panes) {
      this.configureViewerForPane(pane);
      this.installPaneListeners(pane);
      this.applyWarp(pane);
      pane.stage.style.visibility = "visible";
      pane.stage.style.opacity = "1";
      pane.canvas.style.visibility = "visible";
    }

    this.overlayControls.style.visibility = "visible";
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.applyWarp());
    this.resizeObserver.observe(this);
    window.addEventListener("scroll", () => this.applyWarp(), { passive: true });
    if (!this.panListenerInstalled) {
      window.addEventListener("mousemove", (e) => this.onPanMouseMove(e));
      window.addEventListener("mouseup", () => this.onPanMouseUp());
      this.panListenerInstalled = true;
    }
    this.installOverlayControls();
    this.fitRenderOverlayBounds();
    for (const delay of [0, 250, 750, 1500, 3000]) {
      window.setTimeout(() => this.stabilizeAllPaneRendering(), delay);
    }
    setStatus(`ready: ${this.panes.length} photo${this.panes.length === 1 ? "" : "s"} + full KiCanvas UI`);
  }

  private async createPane(viewConfig: RuntimeViewConfig, src: ProjectPage, primary: boolean): Promise<ViewPane> {
    let paneEl: HTMLDivElement;
    let title: HTMLDivElement;
    let stage: HTMLDivElement;
    let photo: HTMLImageElement;
    let canvas: HTMLCanvasElement;
    let viewer: WarpedBoardViewer;

    if (primary) {
      paneEl = this.primaryPaneEl;
      title = this.primaryTitle;
      stage = this.stage;
      photo = this.photo;
      canvas = this.canvas;
      viewer = this.viewer;
    } else {
      photo = html`<img class="photo" alt="PCB photo" />` as HTMLImageElement;
      canvas = html`<canvas class="boardCanvas"></canvas>` as HTMLCanvasElement;
      title = html`<div class="paneTitle"></div>` as HTMLDivElement;
      stage = html`<div class="stage">${photo}${canvas}</div>` as HTMLDivElement;
      paneEl = html`<div class="viewPane extra">${title}${stage}</div>` as HTMLDivElement;
      this.viewsContainer.appendChild(paneEl);
      this.patchCanvasForWarp(canvas);
      viewer = this.addDisposable(new WarpedBoardViewer(canvas, !this.disableinteraction, PILCROW_THEME.board));
      viewer.contextMenuCallback = (_screenX, _screenY, items, onSelect) => {
        this.contextMenu?.show(viewer.lastClientX, viewer.lastClientY, items, onSelect);
      };
      await viewer.setup();
      await viewer.load(src.document);
      await viewer.loaded;
    }

    title.textContent = viewConfig.label ?? (viewConfig.side === "B" ? "Back" : "Front");
    paneEl.dataset["viewId"] = viewConfig.id ?? "";
    paneEl.dataset["side"] = viewConfig.side;
    stage.style.visibility = "hidden";
    stage.style.opacity = "0";
    canvas.style.visibility = "hidden";
    canvas.style.opacity = "0";
    paneEl.scrollLeft = 0;
    paneEl.scrollTop = 0;
    photo.src = viewConfig.photo;

    return { config: viewConfig, pane: paneEl, title, stage, photo, canvas, viewer, photoScale: 1, zoom: 1, opacity: this.opacity };
  }

  private configureViewerForPane(pane: ViewPane) {
    const viewer = pane.viewer;
    viewer.theme = PILCROW_THEME.board;
    viewer.renderer.background_color = Color.transparent_black;
    (viewer.renderer as any).gl?.clearColor(0, 0, 0, 0);
    const desiredFlip = pane.config.side === "B";
    if (viewer.viewport.camera.flipped !== desiredFlip) {
      viewer.flip_view();
    } else {
      viewer.paint();
    }
    viewer.page_opacity = 0;
    viewer.grid_opacity = 0;
    const drawingSheet = viewer.layers.by_name(LayerNames.drawing_sheet);
    if (drawingSheet) {
      drawingSheet.visible = false;
      drawingSheet.opacity = 0;
      drawingSheet.clear();
    }
    const gridLayer = viewer.layers.by_name(LayerNames.grid);
    if (gridLayer) {
      gridLayer.visible = false;
      gridLayer.opacity = 0;
      gridLayer.clear();
    }

    for (const layer of viewer.layers.in_order()) {
      const name = String(layer.name);
      if ([LayerNames.f_fab, LayerNames.b_fab, LayerNames.f_crtyd, LayerNames.b_crtyd, LayerNames.dwgs_user, LayerNames.cmts_user].includes(layer.name as LayerNames)) {
        layer.opacity = 0;
      }
      const isFront = name.startsWith("F.") || name.includes(":Front");
      const isBack = name.startsWith("B.") || name.includes(":Back");
      const isInner = /^In\d+\.Cu$/.test(name) || name.includes(":Inner");
      if (pane.config.side === "F" && (isBack || isInner)) layer.visible = false;
      if (pane.config.side === "B") {
        if (isFront || isInner) layer.visible = false;
        if (isBack) layer.visible = true;
      }
    }

    viewer.lockToRenderBBox(pane.config.render_bbox);
    this.installViewerSelectionListeners(pane);
  }

  private applyPaneLayerSettings(pane: ViewPane) {
    const viewer = pane.viewer;
    viewer.page_opacity = 0;
    viewer.grid_opacity = 0;
    const drawingSheet = viewer.layers.by_name(LayerNames.drawing_sheet);
    if (drawingSheet) {
      drawingSheet.visible = false;
      drawingSheet.opacity = 0;
      drawingSheet.clear();
    }
    const gridLayer = viewer.layers.by_name(LayerNames.grid);
    if (gridLayer) {
      gridLayer.visible = false;
      gridLayer.opacity = 0;
      gridLayer.clear();
    }

    for (const layer of viewer.layers.in_order()) {
      const name = String(layer.name);
      if ([LayerNames.f_fab, LayerNames.b_fab, LayerNames.f_crtyd, LayerNames.b_crtyd, LayerNames.dwgs_user, LayerNames.cmts_user].includes(layer.name as LayerNames)) {
        layer.opacity = 0;
      }
      const isFront = name.startsWith("F.") || name.includes(":Front");
      const isBack = name.startsWith("B.") || name.includes(":Back");
      const isInner = /^In\d+\.Cu$/.test(name) || name.includes(":Inner");
      if (pane.config.side === "F" && (isBack || isInner)) layer.visible = false;
      if (pane.config.side === "B") {
        if (isFront || isInner) layer.visible = false;
        if (isBack) layer.visible = true;
      }
    }
  }

  private stabilizePaneRendering(pane: ViewPane) {
    const desiredFlip = pane.config.side === "B";
    pane.viewer.flip_view();
    pane.viewer.flip_view();
    if (pane.viewer.viewport.camera.flipped !== desiredFlip) pane.viewer.flip_view();
    this.applyPaneLayerSettings(pane);
    pane.viewer.lockToRenderBBox(pane.config.render_bbox);
  }

  private stabilizeAllPaneRendering() {
    for (const pane of this.panes) this.stabilizePaneRendering(pane);
    this.applyWarp();
  }

  private installViewerSelectionListeners(pane: ViewPane) {
    if ((pane.viewer as any).__pilcrowSelectionInstalled) return;
    (pane.viewer as any).__pilcrowSelectionInstalled = true;
    pane.viewer.addEventListener(KiCanvasSelectEvent.type, (e: Event) => {
      if (this.syncingSelection) return;
      const item = (e as KiCanvasSelectEvent).detail.item;
      this.selectedItem = item ?? null;
      if (item) this.highlightedNet = null;
      selectionEl.textContent = item ? `selected: ${itemLabel(item)}` : "";
      this.syncSelectionToPanes(pane, item ?? null);
      this.updateFocusAnimation();
    });
    pane.viewer.addEventListener("pilcrow:net-highlight", (e: Event) => {
      if (this.syncingSelection) return;
      const net = (e as CustomEvent).detail.net as number | null;
      this.highlightedNet = net;
      if (net == null) {
        // Clearing a net highlight should not erase an active component
        // selection/focus. Component selection is driven by KiCanvasSelectEvent.
        if (!this.selectedItem) {
          selectionEl.textContent = "";
          this.syncNetToPanes(pane, null);
        }
        this.updateFocusAnimation();
        return;
      }

      this.selectedItem = null;
      const name = this.viewer.board?.nets[net]?.name ?? `#${net}`;
      selectionEl.textContent = `selected: Net ${name}`;
      this.syncNetToPanes(pane, net);
      this.updateFocusAnimation();
    });
  }

  private syncSelectionToPanes(source: ViewPane, item: any) {
    this.syncingSelection = true;
    try {
      for (const pane of this.panes) {
        if (pane === source) continue;
        pane.viewer.clearNetHighlight(false);
        pane.viewer.select(item ?? null);
      }
      if (source.viewer !== this.viewer && item) this.viewer.select(item);
    } finally {
      this.syncingSelection = false;
    }
  }

  private syncNetToPanes(source: ViewPane, net: number | null) {
    this.syncingSelection = true;
    try {
      for (const pane of this.panes) {
        if (pane === source) continue;
        pane.viewer.select(null);
        if (net == null) pane.viewer.clearNetHighlight(false);
        else pane.viewer.applyNetHighlight(net, false);
      }
      if (source.viewer !== this.viewer) {
        this.viewer.select(null);
        if (net == null) this.viewer.clearNetHighlight(false);
        else this.viewer.applyNetHighlight(net, false);
      }
    } finally {
      this.syncingSelection = false;
    }
  }

  private installPaneListeners(pane: ViewPane) {
    if ((pane.pane as any).__pilcrowInstalled) return;
    (pane.pane as any).__pilcrowInstalled = true;
    pane.pane.addEventListener("wheel", (e) => this.onWheel(e, pane), { passive: false });
    pane.pane.addEventListener("mousemove", (e) => pane.viewer.updateMouseFromClient(e.clientX, e.clientY));
    pane.pane.addEventListener("mousedown", (e) => this.onPanMouseDown(e, pane));
    pane.pane.addEventListener("click", (e) => this.onPaneClick(e, pane));
    pane.pane.addEventListener("scroll", () => this.onPaneScroll(pane), { passive: true });
  }

  private installOverlayControls() {
    if (this.overlayControlsInstalled) return;
    this.overlayControlsInstalled = true;
    this.opacityInput.addEventListener("input", () => {
      this.setOverlayOpacity(Number(this.opacityInput.value));
    });
    this.fitButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.fitRenderOverlayBounds();
    });
    for (const type of ["mousedown", "mouseup", "click", "dblclick", "pointerdown", "pointerup", "contextmenu", "wheel"]) {
      this.overlayControls.addEventListener(type, (e) => e.stopPropagation(), { passive: true });
    }
  }

  setOverlayOpacity(value: number) {
    this.opacity = Math.max(0, Math.min(1, value));
    if (this.opacityInput) this.opacityInput.value = String(this.opacity);
    for (const pane of this.panes) {
      pane.opacity = this.opacity;
      pane.canvas.style.opacity = String(this.opacity);
    }
  }

  setOverlayZoom(value: number, anchor?: { clientX: number; clientY: number }) {
    const pane = this.panes[0];
    if (pane) this.setPaneZoom(pane, value, anchor);
  }

  private onPaneScroll(pane: ViewPane) {
    this.applyWarp(pane, false);
    if (this.syncingPanZoom || this.suppressScrollSync.has(pane)) return;
    this.requestPanZoomSync(pane);
  }

  private setPaneScroll(pane: ViewPane, left: number, top: number, suppressSync = false) {
    const maxScrollX = Math.max(0, pane.stage.clientWidth - pane.pane.clientWidth);
    const maxScrollY = Math.max(0, pane.stage.clientHeight - pane.pane.clientHeight);
    if (suppressSync) {
      this.suppressScrollSync.add(pane);
      window.setTimeout(() => this.suppressScrollSync.delete(pane), 80);
    }
    pane.pane.scrollLeft = Math.max(0, Math.min(maxScrollX, left));
    pane.pane.scrollTop = Math.max(0, Math.min(maxScrollY, top));
  }

  private paneViewportCenterNorm(pane: ViewPane): [number, number] | null {
    const stageW = pane.stage.clientWidth || Math.round((pane.photo.naturalWidth || 1) * pane.photoScale);
    const stageH = pane.stage.clientHeight || Math.round((pane.photo.naturalHeight || 1) * pane.photoScale);
    if (!stageW || !stageH) return null;
    return [
      Math.max(0, Math.min(1, (pane.pane.scrollLeft + pane.pane.clientWidth / 2) / stageW)),
      Math.max(0, Math.min(1, (pane.pane.scrollTop + pane.pane.clientHeight / 2) / stageH)),
    ];
  }

  private centerPaneOnNormalizedPoint(pane: ViewPane, center: [number, number], suppressSync = false) {
    const stageW = pane.stage.clientWidth || Math.round((pane.photo.naturalWidth || 1) * pane.photoScale);
    const stageH = pane.stage.clientHeight || Math.round((pane.photo.naturalHeight || 1) * pane.photoScale);
    this.setPaneScroll(
      pane,
      center[0] * stageW - pane.pane.clientWidth / 2,
      center[1] * stageH - pane.pane.clientHeight / 2,
      suppressSync,
    );
  }

  private requestPanZoomSync(source: ViewPane) {
    if (this.syncingPanZoom || this.panes.length < 2) return;
    this.pendingPanZoomSource = source;
    if (this.syncPanZoomRaf) return;
    this.syncPanZoomRaf = requestAnimationFrame(() => {
      this.syncPanZoomRaf = 0;
      const latestSource = this.pendingPanZoomSource;
      this.pendingPanZoomSource = null;
      if (latestSource) this.syncPanZoomFrom(latestSource);
    });
  }

  private syncPanZoomFrom(source: ViewPane) {
    if (this.syncingPanZoom || this.panes.length < 2) return;
    const sourceCenter = this.paneViewportCenterNorm(source);
    if (!sourceCenter) return;

    this.syncingPanZoom = true;
    try {
      for (const pane of this.panes) {
        if (pane === source) continue;
        pane.zoom = source.zoom;
        this.applyWarp(pane, false);
        this.centerPaneOnNormalizedPoint(pane, sourceCenter, true);
        this.applyWarp(pane, false);
      }
    } finally {
      this.syncingPanZoom = false;
    }
    this.drawFocusOverlay();
  }

  private setPaneZoom(pane: ViewPane, value: number, anchor?: { clientX: number; clientY: number }, sync = true) {
    if (!pane.photo.naturalWidth) return;
    const oldScale = pane.photoScale || 1;
    const rect = pane.pane.getBoundingClientRect();
    const localX = (anchor?.clientX ?? (rect.left + pane.pane.clientWidth / 2)) - rect.left;
    const localY = (anchor?.clientY ?? (rect.top + pane.pane.clientHeight / 2)) - rect.top;
    const photoX = (pane.pane.scrollLeft + localX) / oldScale;
    const photoY = (pane.pane.scrollTop + localY) / oldScale;

    pane.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));
    this.applyWarp(pane, false);

    this.setPaneScroll(pane, photoX * pane.photoScale - localX, photoY * pane.photoScale - localY, true);
    this.applyWarp(pane, false);
    if (sync) this.requestPanZoomSync(pane);
    else this.drawFocusOverlay();
  }

  resetOverlayZoom() {
    this.fitRenderOverlayBounds();
  }

  private renderOverlayPhotoBounds(config: RuntimeViewConfig): Rect | null {
    const r = config.render_bbox;
    const corners = [
      [r.x, r.y],
      [r.x + r.w, r.y],
      [r.x + r.w, r.y + r.h],
      [r.x, r.y + r.h],
    ].map(([x, y]) => project(config.H, x, y));
    const xs = corners.map((p) => p[0]);
    const ys = corners.map((p) => p[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  fitRenderOverlayBounds() {
    for (const pane of this.panes) this.fitRenderOverlayBoundsForPane(pane);
    this.drawFocusOverlay();
  }

  private fitRenderOverlayBoundsForPane(pane: ViewPane) {
    if (!pane.photo.naturalWidth) return;
    const bounds = this.renderOverlayPhotoBounds(pane.config);
    if (!bounds?.w || !bounds?.h) return;

    const nw = pane.photo.naturalWidth;
    const nh = pane.photo.naturalHeight;
    const viewportW = Math.max(320, pane.pane.clientWidth || nw);
    const viewportH = Math.max(240, pane.pane.clientHeight || nh);
    const baseScale = Math.min(1, viewportW / nw, viewportH / nh);
    const margin = 0.86;
    const desiredPhotoScale = Math.min((viewportW * margin) / bounds.w, (viewportH * margin) / bounds.h);
    pane.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, desiredPhotoScale / baseScale));
    this.applyWarp(pane);

    const centerX = (bounds.x + bounds.w / 2) * pane.photoScale;
    const centerY = (bounds.y + bounds.h / 2) * pane.photoScale;
    this.setPaneScroll(pane, centerX - viewportW / 2, centerY - viewportH / 2, true);
    this.applyWarp(pane);
  }

  private onWheel(e: WheelEvent, pane: ViewPane) {
    // Outer photo+overlay zoom. KiCanvas internal camera zoom stays frozen.
    if (!pane.photo.naturalWidth) return;
    e.preventDefault();
    const factor = Math.exp(-e.deltaY * 0.001);
    this.setPaneZoom(pane, pane.zoom * factor, { clientX: e.clientX, clientY: e.clientY });
  }

  private worldFromClient(clientX: number, clientY: number, pane: ViewPane): Vec2 | null {
    return pane.viewer.updateMouseFromClient(clientX, clientY);
  }

  private hasClickableUnder(clientX: number, clientY: number, pane: ViewPane): boolean {
    if (!pane.viewer?.layers) return false;
    const world = this.worldFromClient(clientX, clientY, pane);
    if (!world) return false;
    for (const _ of pane.viewer.layers.query_point(world)) {
      return true;
    }
    return false;
  }

  private onPanMouseDown(e: MouseEvent, pane: ViewPane) {
    if (e.button !== 0 || !pane.photo.naturalWidth) return;
    if (this.hasClickableUnder(e.clientX, e.clientY, pane)) return;
    this.activePan = { pane, lastX: e.clientX, lastY: e.clientY, moved: false };
  }

  private onPanMouseMove(e: MouseEvent) {
    if (!this.activePan) return;
    const state = this.activePan;
    const dx = e.clientX - state.lastX;
    const dy = e.clientY - state.lastY;
    if (Math.hypot(dx, dy) > 1) state.moved = true;
    this.setPaneScroll(state.pane, state.pane.pane.scrollLeft - dx, state.pane.pane.scrollTop - dy, true);
    state.lastX = e.clientX;
    state.lastY = e.clientY;
    this.applyWarp(state.pane, false);
    this.requestPanZoomSync(state.pane);
  }

  private onPanMouseUp() {
    this.suppressNextClick = !!this.activePan?.moved;
    this.activePan = null;
  }

  private clearFocusAndSelection() {
    this.syncingSelection = true;
    try {
      for (const pane of this.panes) {
        pane.viewer.select(null);
        pane.viewer.clearNetHighlight(false);
      }
    } finally {
      this.syncingSelection = false;
    }
    this.selectedItem = null;
    this.highlightedNet = null;
    selectionEl.textContent = "";
    this.updateFocusAnimation();
  }

  private onPaneClick(e: MouseEvent, pane: ViewPane) {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }
    if (pane.viewer.pickAtClient(e.clientX, e.clientY)) return;
    this.clearFocusAndSelection();
  }

  private fitPhotoInViewer(pane: ViewPane): number {
    const nw = pane.photo.naturalWidth || 1;
    const nh = pane.photo.naturalHeight || 1;
    const availableW = Math.max(320, pane.pane.clientWidth || nw);
    const availableH = Math.max(240, pane.pane.clientHeight || nh);
    const baseScale = Math.min(1, availableW / nw, availableH / nh);
    const scale = baseScale * pane.zoom;
    const w = Math.round(nw * scale);
    const h = Math.round(nh * scale);

    pane.stage.style.width = `${w}px`;
    pane.stage.style.height = `${h}px`;
    pane.photo.style.width = `${w}px`;
    pane.photo.style.height = `${h}px`;
    return scale;
  }

  private applyWarp(pane?: ViewPane, redrawFocus = true) {
    const targets = pane ? [pane] : this.panes;
    for (const p of targets) {
      if (!p.photo.naturalWidth) continue;
      p.photoScale = this.fitPhotoInViewer(p);
      const transform = homographyToMatrix3d(p.config.H, 1, p.photoScale);
      p.canvas.style.opacity = String(p.opacity);
      p.canvas.style.transform = transform;
      p.viewer.screenTransform = clientTransformFor(p.stage, p.config.H, p.photoScale);
    }
    if (redrawFocus) this.drawFocusOverlay();
  }

  private syncFocusCanvasSize() {
    const rect = this.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    const rawDpr = window.devicePixelRatio || 1;
    const pixelCapScale = Math.sqrt(FOCUS_CANVAS_MAX_PIXELS / Math.max(1, w * h));
    const dpr = Math.max(0.5, Math.min(rawDpr, FOCUS_CANVAS_MAX_SIDE / w, FOCUS_CANVAS_MAX_SIDE / h, pixelCapScale));

    this.focusCanvas.style.left = `${Math.round(rect.left)}px`;
    this.focusCanvas.style.top = `${Math.round(rect.top)}px`;
    if (this.overlayControls) {
      this.overlayControls.style.left = `${Math.round(rect.left + 12)}px`;
      this.overlayControls.style.top = `${Math.round(rect.top + 12)}px`;
    }
    this.focusCanvas.style.width = `${w}px`;
    this.focusCanvas.style.height = `${h}px`;

    const bw = Math.max(1, Math.round(w * dpr));
    const bh = Math.max(1, Math.round(h * dpr));
    if (this.focusCanvas.width !== bw || this.focusCanvas.height !== bh) {
      this.focusCanvas.width = bw;
      this.focusCanvas.height = bh;
    }
    return { w, h, dpr };
  }

  private stageToViewportPoint(pane: ViewPane, p: [number, number]): [number, number] {
    const stageRect = pane.stage.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();
    return [stageRect.left - hostRect.left + p[0], stageRect.top - hostRect.top + p[1]];
  }

  private worldToViewportPoint(pane: ViewPane, v: Vec2): [number, number] | null {
    if (!pane.viewer?.viewport) return null;
    const local = pane.viewer.viewport.camera.world_to_screen(v);
    const [px, py] = project(pane.config.H, local.x, local.y);
    return this.stageToViewportPoint(pane, [px * pane.photoScale, py * pane.photoScale]);
  }

  private bboxQuadViewport(pane: ViewPane, bb: any, grow: number): [number, number][] | null {
    if (!bb || !pane.viewer?.viewport) return null;
    const x1 = bb.x - grow;
    const y1 = bb.y - grow;
    const x2 = bb.x + bb.w + grow;
    const y2 = bb.y + bb.h + grow;
    const world = [new Vec2(x1, y1), new Vec2(x2, y1), new Vec2(x2, y2), new Vec2(x1, y2)];
    const pts = world.map((p) => this.worldToViewportPoint(pane, p));
    return pts.every(Boolean) ? pts as [number, number][] : null;
  }

  private circlePolygonViewport(pane: ViewPane, center: Vec2, radius: number, segments = 28): [number, number][] | null {
    const pts: ([number, number] | null)[] = [];
    for (let i = 0; i < segments; i++) {
      const a = (i / segments) * Math.PI * 2;
      pts.push(this.worldToViewportPoint(pane, new Vec2(center.x + Math.cos(a) * radius, center.y + Math.sin(a) * radius)));
    }
    return pts.every(Boolean) ? pts as [number, number][] : null;
  }

  private selectedItemSide(): BoardSide | null {
    const layer = this.selectedItem?.layer;
    if (typeof layer !== "string") return null;
    if (layer.startsWith("B.")) return "B";
    if (layer.startsWith("F.")) return "F";
    return null;
  }

  private selectedQuadCss(pane: ViewPane): [number, number][] | null {
    const selectedSide = this.selectedItemSide();
    if (selectedSide && pane.config.side !== selectedSide) return null;
    const bb = this.selectedItem?.bbox ?? (this.selectedItem ? pane.viewer.selected : null);
    if (!bb) return null;
    return this.bboxQuadViewport(pane, bb, Math.max(bb.w * 0.12, bb.h * 0.12, 0.25));
  }

  private netNumberForItem(item: any): number | null {
    if (!item || !("net" in item)) return null;
    return typeof item.net === "number" ? item.net : (item.net?.number ?? null);
  }

  private netFocusShapes(pane: ViewPane): NetFocusShape[] {
    if (this.highlightedNet == null || !pane.viewer?.layers) return [];
    const out: NetFocusShape[] = [];
    const seen = new Set<string>();
    for (const layer of pane.viewer.layers.in_display_order()) {
      if (!layer.visible || layer.name === ":Overlay") continue;
      for (const [item, bbox] of layer.bboxes) {
        if (this.netNumberForItem(item) !== this.highlightedNet || !bbox?.valid) continue;
        const key = `${item?.uuid ?? item?.tstamp ?? item?.number ?? item?.constructor?.name}:${bbox.x.toFixed(3)},${bbox.y.toFixed(3)},${bbox.w.toFixed(3)},${bbox.h.toFixed(3)}`;
        if (seen.has(key)) continue;
        seen.add(key);

        if (item?.start && item?.end) {
          const a = this.worldToViewportPoint(pane, item.start);
          const b = this.worldToViewportPoint(pane, item.end);
          if (a && b) out.push({ kind: "line", a, b });
          continue;
        }

        const cname = item?.constructor?.name;
        if (cname === "Via") {
          const center = item.at?.position as Vec2 | undefined;
          const outerRadius = Number.isFinite(item.size) ? item.size / 2 : Math.min(bbox.w, bbox.h) / 2;
          if (center && outerRadius > 0) {
            const outer = this.circlePolygonViewport(pane, center, outerRadius);
            const drillRadius = Number.isFinite(item.drill) && item.drill > 0 ? item.drill / 2 : 0;
            const inner = drillRadius > 0 ? this.circlePolygonViewport(pane, center, drillRadius, 20) : null;
            if (outer) out.push({ kind: "via", outer, ...(inner ? { inner } : {}) });
          }
          continue;
        }

        if (cname === "Pad") {
          const grow = Math.max(Math.min(bbox.w, bbox.h) * 0.12, 0.035);
          const points = this.bboxQuadViewport(pane, bbox, grow);
          if (points) out.push({ kind: "quad", points });
        }
      }
    }
    return out;
  }

  private updateFocusAnimation() {
    // Static focus overlay. Continuous canvas pulse looked nice but made the
    // full-photo overlay noticeably janky on large/DPR displays.
    if (this.focusRaf) cancelAnimationFrame(this.focusRaf);
    this.focusRaf = 0;
    this.drawFocusOverlay();
  }

  private drawFocusOverlay() {
    if (!this.focusCanvas || !this.panes.length) return;
    const { dpr } = this.syncFocusCanvasSize();
    const ctx = this.focusCanvas.getContext("2d")!;
    const w = this.focusCanvas.width / dpr;
    const h = this.focusCanvas.height / dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const componentFocuses = this.selectedItem
      ? this.panes
        .map((pane) => ({ pane, quad: this.selectedQuadCss(pane) }))
        .filter((focus): focus is { pane: ViewPane; quad: [number, number][] } => Boolean(focus.quad))
      : [];
    const quads = componentFocuses.map((focus) => focus.quad);
    const netShapes = quads.length ? [] : this.panes.flatMap((pane) => this.netFocusShapes(pane));
    if (!quads.length && !netShapes.length) return;

    const pulse = 0.92;

    ctx.save();
    ctx.fillStyle = quads.length ? "rgba(0, 0, 0, 0.36)" : "rgba(0, 0, 0, 0.18)";
    if (quads.length) {
      const hostRect = this.getBoundingClientRect();
      for (const { pane } of componentFocuses) {
        const paneRect = pane.pane.getBoundingClientRect();
        ctx.fillRect(paneRect.left - hostRect.left, paneRect.top - hostRect.top, paneRect.width, paneRect.height);
      }
    } else {
      ctx.fillRect(0, 0, w, h);
    }
    if (quads.length) {
      ctx.globalCompositeOperation = "destination-out";
      for (const quad of quads) {
        ctx.beginPath();
        quad.forEach(([x, y], i) => i ? ctx.lineTo(x, y) : ctx.moveTo(x, y));
        ctx.closePath();
        ctx.fillStyle = "rgba(0,0,0,1)";
        ctx.fill();
      }
    }
    ctx.restore();

    const drawPath = (points: [number, number][]) => {
      points.forEach(([x, y], i) => i ? ctx.lineTo(x, y) : ctx.moveTo(x, y));
      ctx.closePath();
    };

    if (!quads.length) {
      const strokeNet = (stroke: string, lineWidth: number, padWidth: number) => {
        ctx.save();
        ctx.strokeStyle = stroke;
        ctx.fillStyle = stroke;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        for (const shape of netShapes) {
          ctx.beginPath();
          if (shape.kind === "line") {
            ctx.lineWidth = lineWidth;
            ctx.moveTo(shape.a[0], shape.a[1]);
            ctx.lineTo(shape.b[0], shape.b[1]);
            ctx.stroke();
          } else if (shape.kind === "via") {
            ctx.lineWidth = padWidth;
            drawPath(shape.outer);
            ctx.fill();
            ctx.stroke();
            if (shape.inner) {
              ctx.save();
              ctx.globalCompositeOperation = "destination-out";
              ctx.beginPath();
              drawPath(shape.inner);
              ctx.fill();
              ctx.restore();
            }
          } else {
            ctx.lineWidth = padWidth;
            drawPath(shape.points);
            ctx.stroke();
          }
        }
        ctx.restore();
      };
      strokeNet(`rgba(0, 0, 0, ${0.62 * pulse})`, 10, 6);
      strokeNet(`rgba(50, 255, 80, ${pulse})`, 4.2, 3);
      return;
    }

    const drawBracketsForQuad = (quad: [number, number][], stroke: string, width: number) => {
      const cx = quad.reduce((s, p) => s + p[0], 0) / quad.length;
      const cy = quad.reduce((s, p) => s + p[1], 0) / quad.length;
      const expanded = quad.map(([x, y]) => {
        const dx = x - cx;
        const dy = y - cy;
        const len = Math.hypot(dx, dy) || 1;
        return [x + (dx / len) * 7, y + (dy / len) * 7] as [number, number];
      });

      ctx.save();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      for (let i = 0; i < 4; i++) {
        const p = expanded[i];
        const prev = expanded[(i + 3) % 4];
        const next = expanded[(i + 1) % 4];
        const vPrev = [prev[0] - p[0], prev[1] - p[1]];
        const vNext = [next[0] - p[0], next[1] - p[1]];
        const lPrev = Math.hypot(vPrev[0], vPrev[1]) || 1;
        const lNext = Math.hypot(vNext[0], vNext[1]) || 1;
        const tick = Math.max(14, Math.min(34, Math.min(lPrev, lNext) * 0.24));
        ctx.moveTo(p[0], p[1]);
        ctx.lineTo(p[0] + (vPrev[0] / lPrev) * tick, p[1] + (vPrev[1] / lPrev) * tick);
        ctx.moveTo(p[0], p[1]);
        ctx.lineTo(p[0] + (vNext[0] / lNext) * tick, p[1] + (vNext[1] / lNext) * tick);
      }
      ctx.stroke();
      ctx.restore();
    };

    for (const quad of quads) {
      drawBracketsForQuad(quad, `rgba(0, 0, 0, ${0.72 * pulse})`, 10);
      drawBracketsForQuad(quad, `rgba(50, 255, 80, ${pulse})`, 4.4);
    }
  }

}

class WarpedBoardAppElement extends KCBoardAppElement {
  private activeLoadSrc: ProjectPage | null = null;
  private activeLoadPromise: Promise<void> | null = null;

  protected override make_viewer_element(): WarpedBoardViewerElement {
    return html`<warped-board-viewer></warped-board-viewer>` as WarpedBoardViewerElement;
  }

  override renderedCallback(): void | undefined {
    super.renderedCallback();
    requestAnimationFrame(() => this.removeNoopToolbarButtons());
  }

  private removeNoopToolbarButtons(attempt = 0) {
    // KiCanvas's built-in zoom buttons mutate the internal board camera. In this
    // photo-registered mode the camera must stay frozen, so the buttons appear
    // to do nothing useful. Keep the sidebar/panels but remove those controls.
    const bottom = this.renderRoot.querySelector("kc-viewer-bottom-toolbar") as HTMLElement | null;
    const root = bottom?.shadowRoot;
    const buttons = root?.querySelectorAll('kc-ui-button[slot="right"]');
    buttons?.forEach((el) => el.remove());
    if ((!root || !buttons?.length) && attempt < 20) {
      window.setTimeout(() => this.removeNoopToolbarButtons(attempt + 1), 100);
    }
  }

  override async load(src: ProjectPage) {
    if (this.activeLoadSrc === src && this.activeLoadPromise) {
      return this.activeLoadPromise;
    }
    // KCViewerAppElement hides itself until load finishes. Our viewer needs a
    // non-zero layout box before KiCanvas's viewport.ready can resolve, so make
    // the app visible while the warped viewer initializes.
    this.hidden = false;
    // In this custom wrapper the project can finish loading before the base app
    // has resolved viewerReady. The viewer element is already rendered here, so
    // unblock the base load path explicitly.
    if (!this.viewerReady.resolved) this.viewerReady.resolve(true);
    const promise = super.load(src).finally(() => {
      if (this.activeLoadPromise === promise) {
        this.activeLoadPromise = null;
        this.activeLoadSrc = null;
      }
    });
    this.activeLoadSrc = src;
    this.activeLoadPromise = promise;
    return promise;
  }
}

class WarpedKiCanvasOverlayElement extends KCUIElement {
  static override styles = [
    ...KCUIElement.styles,
    new CSS(kcUiStyles),
    css`
      :host {
        display: block;
        width: 100%;
        height: 100vh;
        min-height: 0;
        color: var(--fg);
        background: var(--bg);
        font-family:
          "Nunito", ui-rounded, "Hiragino Maru Gothic ProN",
          Quicksand, Comfortaa, Manjari, "Arial Rounded MT Bold",
          Calibri, source-sans-pro, sans-serif;
      }
      warped-board-app {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ];

  private project = new Project();

  constructor() {
    super();
    provideContext(this, "project", this.project);
  }

  private get warpedViewerElement(): WarpedBoardViewerElement | null {
    return this.renderRoot
      .querySelector("warped-board-app")
      ?.shadowRoot?.querySelector("warped-board-viewer") as WarpedBoardViewerElement | null;
  }

  setOverlayOpacity(value: number) {
    this.warpedViewerElement?.setOverlayOpacity(value);
  }

  setOverlayZoom(value: number) {
    this.warpedViewerElement?.setOverlayZoom(value);
  }

  resetOverlayZoom() {
    this.warpedViewerElement?.resetOverlayZoom();
  }

  fitRenderOverlayBounds() {
    this.warpedViewerElement?.fitRenderOverlayBounds();
  }

  override render() {
    return html`<warped-board-app controls="full" controlslist="nodownload noflipview nofullscreen"></warped-board-app>`;
  }

  override initialContentCallback() {
    void this.loadProject();
  }

  private async loadProject() {
    try {
      setStatus("loading config…");
      const rawConfig = await (await fetch("./config.json")).json() as RawConfig;
      runtimeConfig = normalizeConfig(rawConfig);

      setStatus("loading board…");
      const boardResponse = await fetch(runtimeConfig.board);
      if (!boardResponse.ok) throw new Error(`fetch board: ${boardResponse.status}`);
      setStatus("reading board…");
      const boardText = sanitizeBoardText(await boardResponse.text());
      const boardName = basenameFromUrl(runtimeConfig.board).replace(/\s+/g, "_") || "board.kicad_pcb";
      const boardFile = new File([boardText], boardName, { type: "text/plain" });

      setStatus("parsing board…");
      await this.project.load(new LocalFileSystem([boardFile]));
      this.project.set_active_page(this.project.first_page!);

      // The built-in app can miss the project change if the page is activated
      // before its listener is registered. Force one load; WarpedBoardAppElement
      // dedupes this against any concurrent built-in load.
      await new Promise(requestAnimationFrame);
      const app = this.renderRoot.querySelector("warped-board-app") as WarpedBoardAppElement | null;
      if (app && this.project.active_page) {
        void app.load(this.project.active_page);
      }

      setStatus("loading KiCanvas viewer…");
    } catch (err: any) {
      console.error(err);
      setStatus(`error: ${err?.message || err}`);
    }
  }
}

customElements.define("warped-board-viewer", WarpedBoardViewerElement);
customElements.define("warped-board-app", WarpedBoardAppElement);
customElements.define("warped-kicanvas-overlay", WarpedKiCanvasOverlayElement);

const overlayElement = document.createElement("warped-kicanvas-overlay") as WarpedKiCanvasOverlayElement;
document.getElementById("app")!.appendChild(overlayElement);
